/**
 * Tarjeta "Distribución del dinero cobrado" de la pestaña Estadísticas (presentacional).
 *
 * Réplica de la `stats-unified-card` del SSR (`cotizador_puertas.html`), con el estilo del
 * frontend: a la izquierda la **torta** (CSS `conic-gradient`, sin librerías de gráficas)
 * con el margen neto en el centro y una leyenda de 4 categorías (Costo de material,
 * Ganancia en lámina, Ganancia en mano de obra, IVA); a la derecha el bloque "Tu ganancia
 * real después de todo" (ganancia neta + margen + 4 pasos). Sin lógica de datos: deriva de
 * `CalcResult` con `buildStatsDistribution`/`buildProfitSummary` (réplica numérica del SSR).
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";

import { formatMoney, formatPercent } from "../format";
import {
  buildProfitSummary,
  buildStatsDistribution,
  type ProfitStep,
} from "../statsDerive";
import type { CalcResult } from "../types";

interface StatsDistributionProps {
  /** Última cotización calculada (alimenta la torta y el desglose de ganancia). */
  calc: CalcResult;
}

const STEP_TONE_CLASS: Record<ProfitStep["tone"], string> = {
  neutral: "text-slate-900",
  cost: "text-cyan-600",
  tax: "text-amber-600",
  profit: "text-emerald-600",
};

export function StatsDistribution({ calc }: StatsDistributionProps) {
  const distribution = buildStatsDistribution(calc);
  const profit = buildProfitSummary(calc);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Distribución del dinero cobrado</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Izquierda: torta (conic-gradient) + leyenda de 4 categorías. */}
          <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
            <div
              className="relative h-40 w-40 shrink-0 rounded-full"
              // CSS dinámico inevitable: el gradiente depende de porcentajes calculados.
              style={{ background: distribution.conicGradient }}
              role="img"
              aria-label={`Ganancia neta ${formatPercent(distribution.margenPct)} del total cobrado`}
            >
              <div className="absolute inset-[18%] flex flex-col items-center justify-center rounded-full bg-white text-center">
                <span className="text-xl font-bold text-emerald-600">
                  {formatPercent(distribution.margenPct)}
                </span>
                <span className="px-2 text-[10px] leading-tight text-slate-500">
                  ganancia neta tras costos e IVA
                </span>
              </div>
            </div>
            <dl className="w-full flex-1 space-y-2">
              {distribution.slices.map((slice) => (
                <div
                  key={slice.label}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <dt className="flex items-center gap-2 text-slate-600">
                    <span
                      className="inline-block h-3 w-3 shrink-0"
                      // El color del punto refleja el segmento de la torta.
                      style={{ backgroundColor: slice.color }}
                      aria-hidden="true"
                    />
                    {slice.label}
                  </dt>
                  <dd className="font-semibold text-slate-900">
                    {formatMoney(slice.amount)} ({formatPercent(slice.pct)})
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Derecha: "Tu ganancia real después de todo" (ganancia neta + 4 pasos). */}
          <div className="border-t border-slate-200 pt-5 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-slate-500">
              Tu ganancia real después de todo
            </h3>
            <p className="mt-2 text-3xl font-extrabold leading-none text-emerald-600">
              {formatMoney(profit.gananciaTotal)}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {formatPercent(profit.margenPct)} del total cobrado
            </p>
            <dl className="mt-4 space-y-2">
              {profit.steps.map((step) => (
                <div
                  key={step.label}
                  className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 text-sm last:border-b-0"
                >
                  <dt className="text-slate-600">
                    <span className="mr-1 font-bold text-slate-400">{step.sign}</span>
                    {step.label}
                  </dt>
                  <dd className={`font-semibold ${STEP_TONE_CLASS[step.tone]}`}>
                    {formatMoney(step.amount)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
